import React, { useState } from "react";
import { motion } from "framer-motion";
import { Layers, Loader2, Mail, Lock, User } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export default function AuthScreen() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      toast.success("Welcome to FractalNotes!");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-bg min-h-screen flex items-center justify-center px-6 py-12" data-testid="auth-screen">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="flex items-center gap-2 justify-center mb-8">
          <div className="p-2 rounded-xl bg-purple-600/20 border border-purple-500/30">
            <Layers className="w-5 h-5 text-purple-300" />
          </div>
          <span className="font-display text-xl font-bold text-white">FractalNotes AI</span>
        </div>

        <div className="rounded-2xl bg-surface-card/50 border border-purple-500/20 p-7 grid-noise relative overflow-hidden">
          <h1 className="font-display text-2xl font-bold text-white">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="text-sm text-purple-200/60 mt-1 mb-6">
            {mode === "login" ? "Log in to explore and save your grids." : "Start mapping subjects in seconds."}
          </p>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <div className="relative">
                <User className="w-4 h-4 text-purple-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Name (optional)"
                  className="pl-10 h-12 bg-[#0D0B18] border-purple-500/25 text-white placeholder:text-purple-300/40" />
              </div>
            )}
            <div className="relative">
              <Mail className="w-4 h-4 text-purple-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="auth-email-input" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@school.edu"
                className="pl-10 h-12 bg-[#0D0B18] border-purple-500/25 text-white placeholder:text-purple-300/40" />
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 text-purple-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="auth-password-input" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="Password (min 6 chars)"
                className="pl-10 h-12 bg-[#0D0B18] border-purple-500/25 text-white placeholder:text-purple-300/40" />
            </div>

            {error && <p data-testid="auth-error" className="text-sm text-rose-400">{error}</p>}

            <Button data-testid="auth-submit-btn" type="submit" disabled={loading}
              className="w-full h-12 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-semibold shadow-[0_0_24px_rgba(168,85,247,0.4)]">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : mode === "login" ? "Log in" : "Sign up"}
            </Button>
          </form>

          <button
            data-testid="auth-toggle-btn"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="mt-5 text-sm text-purple-300/70 hover:text-purple-200 w-full text-center"
          >
            {mode === "login" ? "New here? Create an account" : "Already have an account? Log in"}
          </button>

          <div className="mt-4 text-center text-xs text-purple-300/40 border-t border-purple-500/10 pt-3">
            Demo: demo@fractalnotes.ai / demo1234
          </div>
        </div>
      </motion.div>
    </div>
  );
}
